import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import { UserDetailsDTO } from "@reflag/shared/userAPI";

import { userQueryKeys } from "./userQueryKeys";

export function useUserData<TData = UserDetailsDTO>(
  userId?: string,
  options?: Omit<
    UseQueryOptions<UserDetailsDTO, AxiosError<ErrorResponse>, TData>,
    "enabled" | "queryKey" | "queryFn"
  >,
) {
  const { appId, envId } = useEnvironment();

  return useQuery<UserDetailsDTO, AxiosError<ErrorResponse>, TData>({
    queryKey: userQueryKeys.single(appId, envId, userId),

    queryFn: () =>
      api
        .get<"/apps/:appId/envs/:envId/users/:userId">(
          `/apps/${appId}/envs/${envId}/users/${encodeURIComponent(userId!)}`,
        )
        .then((res) => res.data.user),
    retry: (failureCount, error) => {
      if (error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
    enabled: !!appId && !!envId && !!userId,
  });
}
